import React from 'react';
import { FileText } from 'lucide-react';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import type { OfferAttachment } from '../../../types';
import { openPdfDocument } from '../../../utils/loadPdfjs';
import { fetchPdfBytes } from '../../../utils/pdfDocumentBytes';
import { offerAttachmentFileSrc } from '../utils/offerFirstPageImages';

const previewCache = new Map<string, string>();

async function renderFirstPagePreview(url: string): Promise<string> {
  const bytes = await fetchPdfBytes(url);
  const doc = await openPdfDocument(bytes);
  try {
    const page = await doc.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const targetWidth = 360;
    const viewport = page.getViewport({ scale: targetWidth / base.width });
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    const context = canvas.getContext('2d');
    if (!context) throw new Error('canvas');
    await page.render({ canvas, canvasContext: context, viewport }).promise;
    return canvas.toDataURL('image/jpeg', 0.72);
  } finally {
    void doc.destroy();
  }
}

export function OfferAttachmentPdfThumb({
  leadRouteId,
  attachment,
}: {
  leadRouteId: string;
  attachment: OfferAttachment;
}) {
  const cached = previewCache.get(attachment.object_path) ?? null;
  const [preview, setPreview] = React.useState<string | null>(cached);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    if (preview) return;
    let cancelled = false;
    void (async () => {
      try {
        const src = await renderFirstPagePreview(offerAttachmentFileSrc(leadRouteId, attachment.object_path));
        previewCache.set(attachment.object_path, src);
        if (!cancelled) setPreview(src);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attachment.object_path, leadRouteId, preview]);

  if (preview) {
    return <img src={preview} alt={attachment.filename} className="h-full w-full bg-white object-contain" />;
  }

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 bg-slate-100 text-slate-500">
      {failed ? (
        <>
          <FileText className="h-6 w-6" />
          <span className="text-[10px] font-semibold tracking-wide">PDF</span>
        </>
      ) : (
        <LoadingSpinner size="sm" />
      )}
    </div>
  );
}
